import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { http, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loading, StatusBadge } from "@/components/common";
import { toast } from "sonner";
import { ArrowLeft, Phone, MapPin, Mail, MessageSquare, Plus, FolderKanban, FileText, Download } from "lucide-react";

export default function ClienteDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const nav = useNavigate();
  const { t, i18n } = useTranslation();

  const load = () => http.get(`/customers/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, [id]);

  const newProject = async () => {
    try { const r = await http.post("/projects", { customer_id: id }); nav(`/pratiche/${r.data.id}`); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!c) return <Loading />;
  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-5">
      <button onClick={() => nav("/clienti")} className="flex items-center gap-1 text-sm text-slate-500"><ArrowLeft className="w-4 h-4" /> {t("clidet.back")}</button>
      <Card className="p-5 border-slate-200">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-[#0F172A] text-white flex items-center justify-center text-xl font-bold shrink-0">{c.name?.[0]?.toUpperCase()}</div>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-xl font-bold text-[#0F172A]">{c.name} {c.cognome} {c.is_demo && <span className="text-[10px] bg-violet-100 text-violet-700 px-1.5 rounded align-middle">DEMO</span>}</h1>
            <div className="text-sm text-slate-500 space-y-0.5 mt-1">
              <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {c.phone}</div>
              {c.comune && <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {c.comune} {c.indirizzo}</div>}
              {c.email && <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {c.email}</div>}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <a href={`https://wa.me/${c.phone?.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="gap-1.5 w-full"><MessageSquare className="w-4 h-4" /> WhatsApp</Button></a>
            <a href={`tel:${c.phone?.replace(/\s/g, "")}`}><Button size="sm" variant="outline" className="gap-1.5 w-full"><Phone className="w-4 h-4" /> {t("clidet.call")}</Button></a>
          </div>
        </div>
        {c.note && <p className="text-sm text-slate-600 mt-4 bg-slate-50 rounded-lg p-3">{c.note}</p>}
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-[#0F172A] flex items-center gap-2"><FolderKanban className="w-4 h-4" /> {t("nav.pratiche")} ({c.projects?.length || 0})</h2>
        <Button size="sm" onClick={newProject} data-testid="new-project-for-customer" className="gap-1.5 bg-[#0F172A]"><Plus className="w-4 h-4" /> {t("pratiche.new")}</Button>
      </div>
      <div className="space-y-2">
        {(c.projects || []).map((p) => (
          <Card key={p.id} onClick={() => nav(`/pratiche/${p.id}`)} className="p-3 cursor-pointer hover:shadow-md flex items-center gap-3 border-slate-200">
            <div className="flex-1 min-w-0"><div className="font-medium text-[#0F172A] truncate">{p.title}</div><div className="text-xs text-slate-500">{p.openings?.length || 0} {t("clidet.windows_count")} · {new Date(p.created_at).toLocaleDateString(i18n.language)}</div></div>
            <StatusBadge status={p.status} />
          </Card>
        ))}
        {(!c.projects || c.projects.length === 0) && <p className="text-sm text-slate-400">{t("clidet.no_projects")}</p>}
      </div>

      {c.quotes?.length > 0 && (
        <>
          <h2 className="font-heading font-bold text-[#0F172A] flex items-center gap-2"><FileText className="w-4 h-4" /> {t("clidet.quotes")}</h2>
          <div className="space-y-2">{c.quotes.map((q) => (
            <Card key={q.id} onClick={() => nav(`/preventivi/${q.id}`)} className="p-3 cursor-pointer hover:shadow-md flex items-center gap-3 border-slate-200">
              <div className="flex-1"><span className="font-mono text-sm">{q.number}</span> <span className="text-xs text-slate-500">· € {q.total?.toFixed(2)}</span></div>
              <StatusBadge status={q.status} />
            </Card>))}</div>
        </>
      )}
      <a href={`${http.defaults.baseURL}/customers/${id}/export`} download={`cliente-${id}.json`}>
        <Button variant="outline" size="sm" className="gap-1.5"><Download className="w-4 h-4" /> {t("clidet.export_gdpr")}</Button>
      </a>
    </div>
  );
}
